import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Server, Search, RefreshCw, Monitor, HardDrive, Cpu, Trash2, MoreHorizontal } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { computeEffectiveStatus } from "@/lib/utils";

export default function ServersPage() {
  const { profile } = useAuth();
  const [servers, setServers] = useState<any[]>([]);
  const [filteredServers, setFilteredServers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (profile?.tenant_id) {
      fetchServers();
      setupRealtimeSubscription();
    }
  }, [profile]);

  useEffect(() => {
    const filtered = servers.filter(server => 
      server.hostname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      server.ip_address.includes(searchTerm)
    );
    setFilteredServers(filtered);
  }, [servers, searchTerm]);

  const fetchServers = async () => {
    try {
      const { data: serversData, error } = await supabase
        .from('v_server_status')
        .select('*')
        .eq('tenant_id', profile.tenant_id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setServers(serversData || []);
    } catch (error) {
      console.error('Error fetching servers:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleDeleteServer = async (serverId: string, hostname: string) => {
    try {
      const { error } = await supabase
        .from('servers')
        .delete()
        .eq('id', serverId);

      if (error) throw error;

      toast({
        title: "Server deleted",
        description: `${hostname} has been successfully removed from your monitoring.`,
      });
    } catch (error) {
      console.error('Error deleting server:', error);
      toast({
        title: "Error deleting",
        description: "Something went wrong while deleting the server.",
        variant: "destructive",
      });
    }
  };

  const setupRealtimeSubscription = () => {
    if (!profile?.tenant_id) return;

    const channel = supabase
      .channel('servers-realtime')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'servers',
          filter: `tenant_id=eq.${profile.tenant_id}`,
        },
        (payload) => {
          console.log('Realtime server update:', payload);
          fetchServers();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'servers',
          filter: `tenant_id=eq.${profile.tenant_id}`,
        },
        (payload) => {
          console.log('Realtime server insert:', payload);
          fetchServers();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'servers',
          filter: `tenant_id=eq.${profile.tenant_id}`,
        },
        (payload) => {
          console.log('Realtime server delete:', payload);
          setServers(prev => prev.filter(server => server.id !== payload.old.id));
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchServers();
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      online: { variant: "default" as const, className: "bg-success text-success-foreground" },
      offline: { variant: "secondary" as const, className: "" },
      warning: { variant: "default" as const, className: "bg-warning text-warning-foreground" },
      critical: { variant: "destructive" as const, className: "" }
    };
    return variants[status as keyof typeof variants] || variants.offline;
  };

  const formatUptime = (seconds: number | null) => {
    if (!seconds) return 'N/A';
    
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Servers</h1>
        </div>
        <div className="animate-pulse">
          <Card>
            <CardHeader>
              <div className="h-6 bg-muted rounded w-32"></div>
              <div className="h-4 bg-muted rounded w-48"></div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-16 bg-muted rounded"></div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">Servers</h1>
          <p className="text-muted-foreground">
            Beheer en monitor je server infrastructuur
          </p>
        </div>
        <Button onClick={handleRefresh} disabled={refreshing} className="shadow-md hover:shadow-lg transition-all duration-200">
          <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          Vernieuw
        </Button>
      </div>

      <Card className="relative overflow-hidden" style={{ boxShadow: 'var(--shadow-3d)' }}>
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-50" />
        <CardHeader className="relative z-10">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Server Lijst</CardTitle>
              <CardDescription>
                {filteredServers.length} van {servers.length} servers
              </CardDescription>
            </div>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Zoek servers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="relative z-10">
          {filteredServers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Server className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">
                {servers.length === 0 ? "Nog geen servers" : "Geen servers gevonden"}
              </p>
              <p>
                {servers.length === 0 
                  ? "Installeer monitoring agents om je servers te volgen."
                  : "Probeer je zoekopdracht aan te passen."
                }
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Server</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Basic Metrics</TableHead>
                  <TableHead>Network</TableHead>
                  <TableHead>System Status</TableHead>
                  <TableHead>Uptime</TableHead>
                  <TableHead>Last Seen</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredServers.map((server) => (
                  <TableRow key={server.id}>
                    <TableCell>
                      <div className="flex items-center space-x-3">
                        <Monitor className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{server.hostname}</p>
                          <p className="text-sm text-muted-foreground">{server.ip_address}:{server.port}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <Badge {...getStatusBadge(server.online ? 'online' : 'offline')}>
                          {server.online ? 'online' : 'offline'}
                        </Badge>
                        <Badge variant="outline" className="capitalize text-xs">
                          {server.os}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center text-sm">
                          <Cpu className="h-3 w-3 mr-1 text-blue-500" />
                          CPU: {server.cpu_usage?.toFixed(1) || 'N/A'}%
                        </div>
                        <div className="flex items-center text-sm">
                          <Monitor className="h-3 w-3 mr-1 text-green-500" />
                          RAM: {server.memory_usage?.toFixed(1) || 'N/A'}%
                        </div>
                        <div className="flex items-center text-sm">
                          <HardDrive className="h-3 w-3 mr-1 text-purple-500" />
                          Disk: {server.disk_usage?.toFixed(1) || 'N/A'}%
                        </div>
                        {server.load_average && (
                          <div className="flex items-center text-sm text-muted-foreground">
                            Load: {server.load_average?.toFixed(2) || 'N/A'}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {server.network_in_mbps !== undefined && server.network_out_mbps !== undefined && (
                          <div className="text-sm">
                            ↓ {server.network_in_mbps?.toFixed(1) || '0.0'} Mbps
                            <br />
                            ↑ {server.network_out_mbps?.toFixed(1) || '0.0'} Mbps
                          </div>
                        )}
                        {server.ping_latency_ms && (
                          <div className="text-sm text-muted-foreground">
                            Ping: {server.ping_latency_ms?.toFixed(0) || 'N/A'}ms
                          </div>
                        )}
                        {(server.network_errors || server.network_drops) && (
                          <div className="text-xs text-destructive">
                            Errors: {server.network_errors || 0} | Drops: {server.network_drops || 0}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {server.temperature_celsius && (
                          <div className="text-sm">
                            🌡️ {server.temperature_celsius?.toFixed(1) || 'N/A'}°C
                          </div>
                        )}
                        {server.response_time_ms && (
                          <div className="text-sm">
                            ⚡ {server.response_time_ms?.toFixed(0) || 'N/A'}ms
                          </div>
                        )}
                        {server.service_availability && (
                          <div className="text-sm text-green-600">
                            ✓ {server.service_availability?.toFixed(1) || 'N/A'}%
                          </div>
                        )}
                        {server.battery_level_percent && (
                          <div className="text-sm">
                            🔋 {server.battery_level_percent?.toFixed(0) || 'N/A'}%
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {formatUptime(server.uptime_seconds)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {server.last_seen 
                        ? new Date(server.last_seen).toLocaleString()
                        : 'Never'
                      }
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onSelect={(e) => e.preventDefault()}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Verwijder server
                              </DropdownMenuItem>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Server</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete <strong>{server.hostname}</strong>? 
                                  This action cannot be undone and all monitoring data will be permanently deleted.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Annuleren</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteServer(server.id, server.hostname)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Verwijderen
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}